import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { useRefreshSession } from '@/api/bff/auth/use-refresh-session';
import { entityApprovalStatus } from '@/api/entity/types';
import { _getFile } from '@/api/files/queries';
import { _getMyGammaTeamInvitations } from '@/api/gamma-team/queries';
import { userInvitationStatus } from '@/api/gamma-team/types';
import {
  _getAffiliations,
  _getUserEntities,
  useGetUserProfile,
} from '@/api/user-dashboard/queries';
import { ProfileRequirementsIntent } from '@/app/global-modals/profile-requirements-modal';
import {
  getMissingSteps,
  getUserMissingStepsText,
  MissingStep,
} from '@/features/profile-requirements/missing-steps';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { useLogout } from '@/global-hooks/use-logout';
import { PagePaths } from '@/routes/page-paths';
import {
  useEntitySlice,
  useGlobalModalsSlice,
  useSelectedUserConfigSlice,
} from '@/stores/selectors';
import { UserRole } from '@/stores/slices/selected-user-config';

import { useGetSidebarLinks } from '../Sidebar/use-get-sidebar-links';
import {
  collectHrefs,
  getCabinetSwitchPath,
  getPendingEntityInvitations,
  getPendingGammaRowCopy,
  ROLE_CONFIG,
  shouldLoadPendingInvitations,
} from './data';

export const useUserAccount = () => {
  const { user } = useGetUserProfile();
  const { data } = useQuery(_getUserEntities());
  const { data: headshotFile } = useQuery(_getFile(user?.headshotFileId as string));
  const loadPendingInvitations = shouldLoadPendingInvitations(user);
  const { data: gammaInvitations } = useQuery({
    ..._getMyGammaTeamInvitations(),
    enabled: loadPendingInvitations,
  });
  const { data: affiliations } = useQuery({
    ..._getAffiliations(),
    enabled: loadPendingInvitations,
  });

  const { logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreateEntityPage = location.pathname.includes(PagePaths.createEntity);

  const { selectedEntityId, selectedUserRole, setSelectedUserRole, setSelectedEntityId } =
    useSelectedUserConfigSlice();
  const { handleOpen: openModal } = useGlobalModalsSlice();
  const { stepForms, updateStepForm, resetState } = useEntitySlice();

  const [open, setOpen] = useState(false);
  const { mutate: refreshToken } = useRefreshSession();
  const isUserProfileV2Enabled = useFeatureFlag(FeatureFlags.USER_PROFILE_V2);

  const selectedEntity = data?.entities?.find((el) => el.entityId === selectedEntityId);

  const isUserPassAllForms =
    !!user?.profileCompleted && !!user?.hasSubmittedQuestionnaire && user?.kycStatus === 'APPROVED';
  const isBlocked = !!user?.isBlocked;
  const isCreateEntityLocked = !isUserPassAllForms || isBlocked;

  const missingStepsParams = {
    kycStatus: user?.kycStatus,
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire ?? null,
    isProfileFormFilled: !!user?.profileCompleted,
  };
  const createEntityMissingSteps = getMissingSteps(missingStepsParams);

  const pendingGammaInvitation =
    gammaInvitations?.find(
      (invitation) =>
        invitation.status === userInvitationStatus.Created ||
        invitation.status === userInvitationStatus.Accepted,
    ) ?? null;
  const pendingGammaCopy = pendingGammaInvitation
    ? getPendingGammaRowCopy(pendingGammaInvitation.status, getUserMissingStepsText(user))
    : null;
  const pendingEntityInvitations = getPendingEntityInvitations(
    affiliations?.entityAffiliations,
    data?.entities,
  );

  const createEntityTooltipTitle = isBlocked
    ? 'Your profile is blocked'
    : user && !isUserPassAllForms
      ? `Please ${getUserMissingStepsText(user)} to access this feature.`
      : '';

  const isEntityAccessLocked = !user?.profileCompleted || isBlocked;

  const entityAccessTooltipTitle = isBlocked
    ? 'Your profile is blocked'
    : user && !user.profileCompleted
      ? 'Please fill out your profile to access this entity.'
      : '';

  const entityAccessMissingSteps: MissingStep[] = user?.profileCompleted ? [] : ['profile'];

  const explainProfileRequirements = (intent: ProfileRequirementsIntent) => {
    openModal('PROFILE_REQUIREMENTS_MODAL', {
      intent,
      missingSteps:
        intent === 'create-entity' ? createEntityMissingSteps : entityAccessMissingSteps,
      isBlocked,
    });
    setOpen(false);
  };

  const userLinks = useGetSidebarLinks({
    role: 'user',
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    kycStatus: user?.kycStatus,
    isNonAccredited: user?.isNonAccredited,
  });
  const adminLinks = useGetSidebarLinks({
    role: 'admin',
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    kycStatus: user?.kycStatus,
    isNonAccredited: user?.isNonAccredited,
  });

  const entityLinksParams = {
    role: 'entity',
    hasSubmittedQuestionnaire: user?.hasSubmittedQuestionnaire,
    isBlocked: user?.isBlocked,
    kycStatus: user?.kycStatus,
    isNonAccredited: user?.isNonAccredited,
  } as const;
  const entityLinks = useGetSidebarLinks(entityLinksParams);
  const rejectedEntityLinks = useGetSidebarLinks({ ...entityLinksParams, isEntityRejected: true });

  const getCabinetHrefs = (role: UserRole, entityId: string | null) => {
    if (role === 'admin') return collectHrefs(adminLinks);
    if (role === 'user' || !entityId) return collectHrefs(userLinks);

    const { entityApprovalStatus: status } =
      data?.entities?.find((el) => el.entityId === entityId) ?? {};
    const isRejected =
      status === entityApprovalStatus.Rejected || status === entityApprovalStatus.Blocked;
    // The entity links are built before the switch, so they cannot name the next entity's page.
    return [
      ...collectHrefs(isRejected ? rejectedEntityLinks : entityLinks),
      generatePath(PagePaths.manageEntityId, { id: entityId }),
    ];
  };

  const switchRoleAndNavigate = (role: UserRole, entityId: string | null) => {
    setSelectedUserRole(role);
    setOpen(false);

    if (role !== 'entity') {
      updateStepForm({ id: '' });
      setSelectedEntityId(null);
    } else if (entityId) {
      updateStepForm({ id: entityId });
      setSelectedEntityId(entityId);
    }

    const path =
      role === 'entity' && isCreateEntityPage
        ? PagePaths.fundDashboard
        : getCabinetSwitchPath(location, getCabinetHrefs(role, entityId));
    if (path) {
      navigate(path, { flushSync: true, replace: true });
    }
  };

  const handleSelectEntity = (entityId: string) => {
    if (isEntityAccessLocked) {
      explainProfileRequirements('switch-entity');
      return;
    }

    const entity = data?.entities?.find((el) => el.entityId === entityId);
    refreshToken(
      { entityId, currentSession: 'Entity' },
      {
        onSuccess: () => {
          switchRoleAndNavigate('entity', entityId);
          if (
            entity &&
            (entity.entityApprovalStatus === entityApprovalStatus.Rejected ||
              entity.entityApprovalStatus === entityApprovalStatus.Blocked)
          ) {
            openModal('ENTITY_STATUS_ALERT_MODAL', {
              entityName: entity.entityName,
              status: entity.entityApprovalStatus,
              reason: entity.blockReason,
            });
          }
        },
      },
    );
  };

  const handleRoleClick = (role: UserRole) => {
    refreshToken(
      { entityId: null, currentSession: role === 'user' ? 'User' : 'Admin' },
      { onSuccess: () => switchRoleAndNavigate(role, selectedEntityId) },
    );
  };

  const handleCreateEntity = () => {
    if (isCreateEntityLocked) {
      explainProfileRequirements('create-entity');
      return;
    }

    navigate(PagePaths.createEntityAccount);
    setSelectedEntityId(null);
    setSelectedUserRole('user');
    resetState();
    setOpen(false);
  };

  useEffect(() => {
    if (stepForms.id) return;
    if (selectedUserRole === 'entity' && selectedEntityId) {
      updateStepForm({ id: selectedEntityId });
    }
  }, [selectedUserRole, stepForms.id, selectedEntityId, updateStepForm]);

  const roleConfig = ROLE_CONFIG[selectedUserRole ?? 'user'];
  const badgeLabel =
    selectedUserRole === 'entity' ? (selectedEntity?.entityName ?? 'Entity') : roleConfig.label;

  const ownProfileHref =
    isUserProfileV2Enabled && user?.id && selectedUserRole !== 'entity'
      ? generatePath(PagePaths.userProfile, { id: user.id })
      : null;

  return {
    open,
    setOpen,
    user,
    headshotFile,
    entities: data?.entities,
    logout,
    selectedEntity,
    selectedEntityId,
    selectedUserRole,
    isCreateEntityLocked,
    createEntityTooltipTitle,
    isEntityAccessLocked,
    entityAccessTooltipTitle,
    handleRoleClick,
    handleSelectEntity,
    handleCreateEntity,
    roleConfig,
    badgeLabel,
    ownProfileHref,
    pendingGammaInvitation,
    pendingGammaNote: pendingGammaCopy?.note ?? '',
    pendingGammaTooltip: pendingGammaCopy?.tooltip ?? '',
    pendingEntityInvitations,
  };
};
